import { Coins, Flame, Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import type { LifecycleRewards } from "@/types/mission-detail"

/**
 * OKS-478 — Reward chips for the §08 approved-celebration view.
 *
 * Renders the rewards ACTUALLY granted for the collab (read server-side from
 * the XP / coin ledgers + the membership streak — never hardcoded). The chips
 * fade + scale in on mount via a CSS-only animation (`animate-in`, no JS
 * spring) per UX Spec §4.
 *
 * Honesty note (TRD KD-3): `streak_days` is the creator's CURRENT streak
 * (a membership-level value), NOT a per-collab "+1" delta — so the streak
 * chip reads the streak STATE ("🔥 {N}-day streak"), while XP / coins read
 * the true per-collab grant ("+{N} XP" / "+{N} coins").
 */
export interface RewardChipsProps {
  rewards: LifecycleRewards
  className?: string
}

interface Chip {
  key: string
  icon: typeof Sparkles
  label: string
  testid: string
}

export function RewardChips({ rewards, className }: RewardChipsProps) {
  const { t } = useTranslation()

  const chips: Chip[] = [
    {
      key: "xp",
      icon: Sparkles,
      label: t("creator_mission_detail.lifecycle.approved.reward_xp", {
        count: rewards.xp,
      }),
      testid: "mission-detail-reward-chip-xp",
    },
    {
      key: "coins",
      icon: Coins,
      label: t("creator_mission_detail.lifecycle.approved.reward_coins", {
        count: rewards.coins,
      }),
      testid: "mission-detail-reward-chip-coins",
    },
    {
      key: "streak",
      icon: Flame,
      label: t("creator_mission_detail.lifecycle.approved.reward_streak", {
        count: rewards.streak_days,
      }),
      testid: "mission-detail-reward-chip-streak",
    },
  ]

  return (
    <ul
      className={cn("flex flex-wrap gap-2", className)}
      data-testid="mission-detail-reward-chips"
    >
      {chips.map((chip) => {
        const Icon = chip.icon
        return (
          <li
            key={chip.key}
            data-testid={chip.testid}
            className="animate-in fade-in zoom-in-95 bg-primary/10 text-primary flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold duration-300"
          >
            <Icon className="size-4" aria-hidden="true" />
            {chip.label}
          </li>
        )
      })}
    </ul>
  )
}
